import {
  FREQUENCY_OPTIONS,
  RedemptionBy,
  REMDEMPTION_BY_OPTIONS,
  TransactionOrderType,
} from "creso-core/constants";
import {
  useGetHoldingFolios,
  useHoldingsData,
  useCreateSwpTransaction,
  useUpdateSwpTransaction,
} from "creso-core/hooks";
import { isSWPOrder } from "creso-core/utils";
import {
  FormField,
  FormItem,
  FormLabel,
  V2Loader,
  FormSelect,
  Form,
  FormInput,
  FormCheckbox,
  FormMessage,
  FormDescription,
  FormCurrencyInput,
} from "web-design-system";
import { useCallback, useContext, useMemo } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { swpFormValidationSchema } from "creso-core/zod";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parse } from "date-fns";
import { handleFormValidationErrors } from "@/utils/error";
import {
  HoldingDetails,
  HoldingFoliosDropdown,
  HoldingSchemesDropdown,
  PlanStartDate,
} from "web-core";
import { TransactionContext } from "./transaction-wizard";
import FormActions from "./form-actions";

type FormData = z.infer<typeof swpFormValidationSchema>;

function getDefaultFormData(): FormData {
  return {
    withdrawal_by: RedemptionBy.AMOUNT,
    amount: null,
    units: null,
    number_of_withdrawals: "",
    start_date: "",
    frequency: "MONTHLY",
    scheme: null,
    folio: null,
    first_order_today: true,
  };
}

export default function SWPForm() {
  const {
    selectedOrder,
    investment_account_guid = "",
    updateContext,
  } = useContext(TransactionContext);

  const { data: holdingSchemes, isLoading: isHoldingSchemesLoading } =
    useHoldingsData(investment_account_guid);

  const scheme_isin =
    selectedOrder?.type === TransactionOrderType.SWP
      ? selectedOrder.scheme_isin
      : "";

  const { data: holdingFolios } = useGetHoldingFolios(
    investment_account_guid,
    scheme_isin || "",
    !!scheme_isin && !!investment_account_guid
  );

  const isCancelOrder = selectedOrder?.action_type === "CANCEL";

  const form = useForm({
    resolver: zodResolver(swpFormValidationSchema),
    values: isSWPOrder(selectedOrder)
      ? {
          withdrawal_by: selectedOrder.units
            ? RedemptionBy.UNITS
            : RedemptionBy.AMOUNT,
          amount: selectedOrder.amount ?? null,
          units: selectedOrder.units ?? null,
          number_of_withdrawals:
            selectedOrder.number_of_withdrawals?.toString() || "",
          start_date: selectedOrder.start_date,
          frequency: selectedOrder.frequency || "MONTHLY",
          scheme:
            holdingSchemes?.find(
              (d) => d.scheme_isin === selectedOrder.scheme_isin
            ) ?? null,
          folio:
            holdingFolios?.find((d) => d.folio_id === selectedOrder.folio_id) ??
            null,
          first_order_today: selectedOrder.first_order_today ?? true,
        }
      : getDefaultFormData(),
    disabled: isCancelOrder,
  });

  const createSwpTransaction = useCreateSwpTransaction(investment_account_guid);

  const updateSwpTransaction = useUpdateSwpTransaction(investment_account_guid);

  const onSubmit = useCallback(
    async (values: ReturnType<typeof getDefaultFormData>) => {
      try {
        if (values.scheme?.scheme_isin && values.folio) {
          const payload = {
            investment_account_guid,
            scheme_isin: values.scheme?.scheme_isin,
            folio_id: values.folio?.folio_id || -1,
            number_of_withdrawals: values.number_of_withdrawals.toString(),
            frequency: values.frequency ?? "MONTHLY",
            start_date: values.start_date,
            first_order_today: Boolean(values.first_order_today),
            ...(values.withdrawal_by === RedemptionBy.UNITS
              ? {
                  units: values.units,
                }
              : {
                  amount: values.amount,
                }),
          };

          if (selectedOrder?.id) {
            await updateSwpTransaction.mutateAsync({
              id: selectedOrder.id,
              ...payload,
            });
          } else {
            await createSwpTransaction.mutateAsync(payload);
          }

          form.reset(getDefaultFormData());

          updateContext?.({
            selectedOrder: null,
          });
        }
      } catch (error) {
        handleFormValidationErrors(form, error);
        toast.error(`Failed to save order details`, {
          description: (error as Error).message,
        });
      }
    },
    [
      investment_account_guid,
      createSwpTransaction,
      selectedOrder,
      updateSwpTransaction,
      updateContext,
      form,
    ]
  );

  const startDate = form.watch("start_date");

  const nextInstallmentDate = useMemo(() => {
    try {
      const startDateParsed = parse(startDate, "yyyy-MM-dd", new Date());
      return format(startDateParsed, "dd MMM yyyy");
    } catch {
      return "";
    }
  }, [startDate]);

  return (
    <div className="flex flex-col flex-1">
      <h5 className="mt-1">
        {selectedOrder?.id
          ? isCancelOrder
            ? "SWP Cancellation"
            : "Update SWP order"
          : "Create SWP order"}
      </h5>
      <div className="h-px bg-border w-full mt-3 mb-4"></div>
      <V2Loader className="relative" isLoading={isHoldingSchemesLoading}>
        {isCancelOrder && (
          <div className="z-50 size-full bg-background/50 absolute top-0 left-0" />
        )}
        <Form {...form}>
          <form
            className="space-y-4 flex-1 px-1 flex flex-col items-start"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="grid grid-cols-2 w-full gap-2 pb-4 border-b border-dashed border-border">
              <FormField
                className="col-span-1"
                control={form.control}
                name="scheme"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scheme</FormLabel>
                    <HoldingSchemesDropdown
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        form.setValue("folio", null);
                      }}
                      investmentAccountGuid={investment_account_guid}
                      schemefilter={(scheme) => scheme.swp_allowed}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                className="col-span-1"
                control={form.control}
                name="folio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Folio</FormLabel>
                    <HoldingFoliosDropdown
                      value={field.value}
                      onChange={field.onChange}
                      investmentAccountGuid={investment_account_guid}
                      schemeIsin={form.watch("scheme")?.scheme_isin}
                      filter={(folio) => folio.swp_allowed}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("folio") && (
                <HoldingDetails
                  amount={form.watch("folio")?.amount || ""}
                  units={form.watch("folio")?.units || ""}
                  nav={form.watch("folio")?.nav || ""}
                />
              )}
            </div>
            <h6>Installment details</h6>
            <div className="grid grid-cols-2 w-full gap-2">
              <FormField
                className="col-span-1"
                control={form.control}
                name="number_of_withdrawals"
                render={({ field }) => (
                  <FormInput
                    label="Withdrawal count"
                    type="number"
                    {...field}
                    value={field.value.toString()}
                  />
                )}
              />
              <FormField
                className="col-span-1"
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormSelect
                    options={FREQUENCY_OPTIONS}
                    {...field}
                    label="Frequency"
                    onChange={(value) => {
                      field.onChange(value);
                      form.setValue("start_date", "");
                      if (value === "DAILY") {
                        form.setValue("first_order_today", false);
                      }
                    }}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-10 w-full gap-2">
              <FormField
                className="col-span-3"
                control={form.control}
                name="withdrawal_by"
                render={({ field }) => (
                  <FormSelect
                    label="Withdrawal by"
                    options={REMDEMPTION_BY_OPTIONS}
                    {...field}
                  />
                )}
              />
              {form.watch("withdrawal_by") === RedemptionBy.UNITS ? (
                <FormField
                  className="col-span-3"
                  control={form.control}
                  name="units"
                  render={({ field }) => (
                    <FormInput
                      type="number"
                      {...field}
                      label="Units"
                      value={field.value || ""}
                    />
                  )}
                />
              ) : (
                <FormField
                  className="col-span-3"
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormCurrencyInput
                      label="Amount"
                      {...field}
                      value={field.value || ""}
                    />
                  )}
                />
              )}
              <FormField
                control={form.control}
                className="col-span-4"
                name="start_date"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Start date</FormLabel>
                    <PlanStartDate
                      planType="SWP"
                      firstOrderToday={form.watch("first_order_today")}
                      frequency={form.watch("frequency")}
                      placeholder="Select start date"
                      onChange={field.onChange}
                      value={field.value}
                    />
                    {field.value && (
                      <FormDescription>
                        {form.watch("first_order_today") ? "Next" : "First"}{" "}
                        installment on {nextInstallmentDate}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              className="col-span-1"
              control={form.control}
              name="first_order_today"
              render={({ field }) => (
                <FormCheckbox
                  label="First order today"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={form.watch("frequency") === "DAILY"}
                />
              )}
            />
            <FormActions
              dirty={form.formState.isDirty}
              onReset={() => form.reset()}
              isUpdate={!!selectedOrder?.id}
            />
          </form>
        </Form>
      </V2Loader>
    </div>
  );
}
